import { type FunctionComponent, useCallback, useEffect, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import type { Swiper as SwiperType } from "swiper";
import { Mousewheel, Navigation } from "swiper/modules";
import type { SwiperRef } from "swiper/react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { useVideoShortCarouselContext } from "scmp-app/components/video/video-short-carousel/contexts";
import { VideoShortCarouselPlayer } from "scmp-app/components/video/video-short-carousel/video-short-carousel-player";
import type { videoShortCarouselPlayerModal$key } from "scmp-app/queries/__generated__/videoShortCarouselPlayerModal.graphql";

import {
  CloseButton,
  Container,
  NavButton,
  StyledCloseButtonIcon,
  StyledModal,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperWrapper,
  VideoBackdrop,
} from "./styles";

type Props = {
  className?: string;
  reference: videoShortCarouselPlayerModal$key;
};

export const VideoShortCarouselPlayerModal: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const nodes = useFragment(
    graphql`
      fragment videoShortCarouselPlayerModal on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Video {
            entityId
          }
          ...videoShortCarouselPlayerContent
        }
      }
    `,
    reference,
  );
  const { closePlayerModal, modalState, sendVideoSysTracking, updateModalState } =
    useVideoShortCarouselContext();

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const swiperReference = useRef<SwiperRef>(null);

  useEffect(() => {
    const swiper = swiperReference.current?.swiper;
    if (!modalState?.isOpen || !notEmpty(swiper)) return;

    const updateSlidesOffset = () => {
      const containerHeight = swiper.el.clientHeight;
      const lastSlide = swiper.slides.at(-1);
      const slideHeight = lastSlide?.clientHeight ?? 0;
      swiper.params.slidesOffsetAfter = Math.max(0, containerHeight - slideHeight);
      swiper.updateSlides();
    };

    updateSlidesOffset();
    window.addEventListener("resize", updateSlidesOffset);
    return () => window.removeEventListener("resize", updateSlidesOffset);
  }, [modalState?.isOpen]);

  useEffect(() => {
    const swiper = swiperReference.current?.swiper;
    if (!modalState?.isOpen || !notEmpty(swiper)) return;

    if (swiper.activeIndex !== modalState?.activeSlide) {
      swiper.slideTo(modalState?.activeSlide, 0);
    }
  }, [modalState?.isOpen, modalState?.activeEntityId, nodes, modalState?.activeSlide]);

  const handleSetTranslate = useCallback(
    async (swiper: SwiperType) => {
      await sendVideoSysTracking?.("next");

      const currentNodeId = nodes[swiper.activeIndex]?.node.entityId;
      if (!notEmpty(currentNodeId)) return;

      updateModalState?.(currentNodeId, swiper.activeIndex);
    },
    [nodes, sendVideoSysTracking, updateModalState],
  );

  if (!nodes || nodes.length === 0) return null;

  return (
    <StyledModal
      BackdropComponent={VideoBackdrop}
      keepMounted={true}
      open={modalState?.isOpen ?? false}
    >
      <Container className={className}>
        <CloseButton onClick={closePlayerModal}>
          <StyledCloseButtonIcon />
        </CloseButton>
        <SwiperWrapper>
          <StyledSwiper
            allowTouchMove={true}
            centeredSlides={false}
            cssMode={false}
            direction="vertical"
            initialSlide={0}
            longSwipesRatio={0.1}
            modules={[Navigation, Mousewheel]}
            mousewheel={{
              forceToAxis: true,
              releaseOnEdges: true,
              sensitivity: 1,
            }}
            navigation={{
              nextEl: ".swiper-button.player-next-button",
              prevEl: ".swiper-button.player-prev-button",
            }}
            onTransitionEnd={handleSetTranslate}
            pagination={false}
            ref={swiperReference}
            slidesOffsetBefore={isTabletUp ? 56 : 0}
            slidesPerGroup={1}
            slidesPerView="auto"
            spaceBetween={isTabletUp ? 32 : 16}
            speed={300}
          >
            {nodes.map(({ node }, index) => (
              <StyledSwiperSlide key={`${node.entityId}-${index}`}>
                <VideoShortCarouselPlayer onClose={closePlayerModal} reference={node} />
              </StyledSwiperSlide>
            ))}
          </StyledSwiper>
          <NavButton $direction="top" className="swiper-button player-prev-button" />
          <NavButton $direction="bottom" className="swiper-button player-next-button" />
        </SwiperWrapper>
      </Container>
    </StyledModal>
  );
};

VideoShortCarouselPlayerModal.displayName = "VideoShortCarouselPlayerModal";
